import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import {
  getHoliday,
  updateHoliday,
  getHolidayOptions,
} from "services/hrms.api";
import Loading from "components/common/Loading";

export default function HolidayEditPage() {
  const nav = useNavigate();
  const { id } = useParams();

  const [options, setOptions] = useState({ types: [], regions: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    date: "",
    name: "",
    type: "",
    region: "",
  });

  useEffect(() => {
    (async () => {
      try {
        // load options
        let types = ["National", "Festival", "Company"];
        let regions = ["All"];
        try {
          const meta = await getHolidayOptions();
          types = meta?.data?.types?.length ? meta.data.types : types;
          const rawRegions = meta?.data?.regions?.length
            ? meta.data.regions
            : regions;
          regions = rawRegions.includes("All")
            ? rawRegions
            : ["All", ...rawRegions];
        } catch {}

        // load existing holiday
        const { data } = await getHoliday(id);
        setOptions({ types, regions });
        setForm({
          date: normalizeToYMD(data?.date),
          name: data?.name || "",
          type: data?.type || types[0],
          region: data?.region || regions[0],
        });
      } catch (e) {
        setErr(e?.response?.data?.message || "Failed to load holiday.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const setField = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await updateHoliday(id, form);
      nav("/admin/hrms/holidays");
    } catch (e2) {
      setErr(e2?.response?.data?.message || "Failed to update holiday.");
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => nav(-1)}
          className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm hover:bg-black dark:border-white/10 dark:text-white"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <h2 className="text-2xl font-semibold dark:text-white">Edit Holiday</h2>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-800">
        {loading ? (
          <Loading variant="dots" fullscreen />
        ) : (
          <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
            {err && (
              <div className="bg-rose-50 text-rose-700 rounded-lg p-3 text-sm md:col-span-2">
                {err}
              </div>
            )}

            <Field label="Date">
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={setField}
                required
                className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </Field>

            <Field label="Name">
              <input
                name="name"
                value={form.name}
                onChange={setField}
                required
                className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </Field>

            <Field label="Type">
              <select
                name="type"
                value={form.type}
                onChange={setField}
                className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                {(options.types?.length
                  ? options.types
                  : ["Festival", "National", "Company"]
                ).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Region">
              <select
                name="region"
                value={form.region}
                onChange={setField}
                className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                {(options.regions?.length ? options.regions : ["All"]).map(
                  (r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  )
                )}
              </select>
            </Field>

            <div className="flex justify-end gap-3 md:col-span-2">
              <button
                type="button"
                onClick={() => nav("/admin/hrms/holidays")}
                className="rounded-lg border px-4 py-2 text-sm hover:bg-black focus:outline-none focus:ring-2 focus:ring-brand-500 dark:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-lg bg-gradient-to-r from-yellow-500 to-red-500 px-5 py-2 text-sm text-white hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                Update
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-white/80">
        {label}
      </label>
      {children}
    </div>
  );
}

function normalizeToYMD(v) {
  const d =
    typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)
      ? new Date(v + "T00:00:00")
      : new Date(v);
  if (isNaN(d)) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
